import { Box, Collapse } from "@mui/material";
import clsx from "clsx";
import React from "react";
import { PreservedIcon } from "../../../../assets/images/icons/PreservedIcon";
import BriaTooltip from "../../../../components/common/BriaTooltip/BriaTooltip";
import FiboColorPicker, { RGBColor } from "../../../../layout/LabsLayout/Results/Canvas/components/FiboColorPicker";
import type { JSONValue } from "../../../../models/fibo";
import { isVglNumber, vglNumber } from "../../../../utils/vglTokens";
import styles from "./FiboPromptTree.module.scss";

// Normalize color to short format { r, g, b }. Channels may be numbers or model
// tokens ("<color_222>") — an unparseable channel falls back to 0 rather than NaN.
export const normalizeColor = (v: unknown): RGBColor => {
	const obj = v as Record<string, unknown>;
	if (isVglNumber(obj.r)) {
		return { r: vglNumber(obj.r) ?? 0, g: vglNumber(obj.g) ?? 0, b: vglNumber(obj.b) ?? 0 };
	}
	return { r: vglNumber(obj.red) ?? 0, g: vglNumber(obj.green) ?? 0, b: vglNumber(obj.blue) ?? 0 };
};

interface ColorsTreeNodeProps {
	k: string;
	v: JSONValue;
	path: string[];
	subtreeChanged: boolean;
	isPreserved: boolean;
	collapsible?: boolean;
	isEditable?: boolean;
	onValueChange?: (path: string[], value: JSONValue) => void;
}

export const ColorsTreeNode = ({
	k,
	v,
	path,
	subtreeChanged,
	isPreserved,
	collapsible = false,
	isEditable,
	onValueChange,
}: ColorsTreeNodeProps) => {
	const [open, setOpen] = React.useState(true);
	const colors = v as unknown[];

	const handleColorChange = (index: number, color: RGBColor) => {
		if (!onValueChange) return;
		const newColors = [...colors];
		const originalColor = colors[index] as Record<string, unknown>;

		// Check if original uses long format (red, green, blue) and preserve extra properties
		if (isVglNumber(originalColor.red)) {
			newColors[index] = { ...originalColor, red: color.r, green: color.g, blue: color.b };
		} else {
			newColors[index] = { ...originalColor, r: color.r, g: color.g, b: color.b };
		}
		onValueChange(path, newColors as unknown as JSONValue);
	};

	const colorsContent = (
		<>
			<Box className={styles.colorsArrayContainer}>
				{colors.map((color, i) => (
					<FiboColorPicker
						key={i}
						mode="rgb"
						color={normalizeColor(color)}
						index={i}
						disabled={!isEditable}
						onChange={handleColorChange}
					/>
				))}
			</Box>
			<Box className={styles.header}>
				<span className={styles.value}>
					<span>{`}]`}</span>
				</span>
			</Box>
		</>
	);

	return (
		<Box
			className={clsx(styles.row, collapsible && styles.group, {
				[styles.isChanged]: subtreeChanged && !isPreserved,
				[styles.isPreserved]: isPreserved,
			})}
			onClick={(e) => e.stopPropagation()}
		>
			<Box
				className={styles.header}
				role={collapsible ? "button" : undefined}
				tabIndex={collapsible ? 0 : -1}
				aria-expanded={collapsible ? open : undefined}
				onClick={collapsible ? () => setOpen((o) => !o) : undefined}
			>
				{collapsible && <span className={clsx(styles.chevron, open && styles.open)} aria-hidden />}
				<span className={clsx(styles.key, styles.mainTitle)}>{k.replace(/_/g, " ")}</span>
				<span className={styles.value}>
					<span>{`[{`}</span>
				</span>
				{isPreserved && (
					<BriaTooltip title="Preserved during refinement">
						<span className={styles.preservedIconWrap}>
							<PreservedIcon className={styles.preservedIcon} />
						</span>
					</BriaTooltip>
				)}
			</Box>
			{collapsible ? (
				<Collapse in={open} timeout={220} collapsedSize={0}>
					<Box className={open ? styles.collapseInnerOpen : styles.collapseInner}>{colorsContent}</Box>
				</Collapse>
			) : (
				colorsContent
			)}
		</Box>
	);
};
